<template>
  <Transition
    enter-active-class="transition-all duration-300 ease-out"
    enter-from-class="opacity-0 translate-y-2"
    enter-to-class="opacity-100 translate-y-0"
    leave-active-class="transition-all duration-200 ease-in"
    leave-from-class="opacity-100 translate-y-0"
    leave-to-class="opacity-0 translate-y-2"
  >
    <div
      v-if="!rightyStore.isDrawerOpen && !rightyStore.isFloatingUiHidden"
      class="fixed bottom-6 right-6 z-[1200] flex items-center gap-2"
    >
      <!-- Contextual prompt chips - hidden on mobile -->
      <template v-if="showExternalPromptChips">
        <button
          v-for="(prompt, index) in prompts"
          :key="index"
          @click="handlePromptClick(prompt)"
          class="group hidden items-center gap-2 rounded-full bg-slate-800/70 px-4 py-2.5 text-sm font-medium text-white/90 backdrop-blur-sm transition-all duration-200 hover:bg-slate-800/90 hover:text-white hover:scale-[1.02] active:scale-[0.98] md:flex"
        >
          <SparklesIcon class="h-4 w-4 text-indigo-400 transition-transform group-hover:rotate-12" />
          <span>{{ prompt.label }}</span>
        </button>
      </template>

      <!-- Righty trigger button (always visible) -->
      <div class="glow-trigger relative">
        <button
          @click="rightyStore.toggleDrawer"
          class="relative z-10 flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-[#1e293b] p-1 shadow-lg transition-all duration-200 hover:scale-110"
          title="Open Righty AI Assistant"
        >
          <img src="/img/righty-dark.jpg" alt="Righty AI Assistant" class="h-full w-full rounded-full object-cover" />
          <span class="sr-only">Open Righty AI Assistant</span>
        </button>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { SparklesIcon } from '@heroicons/vue/24/solid'
import { ROUTES } from '@/router/routeNames'
import { useRightyStore } from '../stores/rightyStore'
import { useFlowStore } from '@/modules/flow/stores/flowStore'
import { useOnboardingStore } from '@/modules/onboarding/stores/onboardingStore'
import { useResourceInfo } from '../composables/useResourceInfo'
import type { RightyPrompt } from '../types'

const route = useRoute()
const rightyStore = useRightyStore()
const flowStore = useFlowStore()
const onboardingStore = useOnboardingStore()
const { shouldShowDashboardExperience } = storeToRefs(onboardingStore)
const { interpolatePrompt } = useResourceInfo()

// Use external chip prompts from the store (merges dynamic + route prompts, max 3)
const prompts = computed<RightyPrompt[]>(() => rightyStore.externalChipPrompts)
const showExternalPromptChips = computed(() =>
  route.name !== ROUTES.DASHBOARD || shouldShowDashboardExperience.value
)

async function handlePromptClick(prompt: RightyPrompt) {
  // Handle auto-actions that execute before AI interaction
  if (prompt.autoAction) {
    await handleAutoAction(prompt)
    return
  }

  // Normal prompt handling - open Righty and send message
  rightyStore.openDrawer()

  // Always start a new conversation when clicking a chip prompt
  await rightyStore.startNewConversation()

  // Send the full prompt with route params interpolated
  const finalPrompt = interpolatePrompt(prompt.prompt)
  await rightyStore.sendMessage(finalPrompt)
}

const router = useRouter()

/**
 * Handle auto-actions that execute immediately when a prompt is clicked.
 * These create resources and redirect BEFORE sending a message to Righty.
 */
async function handleAutoAction(prompt: RightyPrompt) {
  const { autoAction } = prompt

  if (autoAction === 'create_quiz_flow' || autoAction === 'create_survey_flow') {
    const flowType = autoAction === 'create_quiz_flow' ? 'Quiz' : 'Survey'

    // Create the flow immediately
    const newFlow = flowStore.create({ name: `New ${flowType}` })

    // Redirect to the new flow using named route with proper params
    const teamPid = route.params.teamPid as string
    await router.push({
      name: ROUTES.OFFERFUNNEL,
      params: { teamPid, offerFunnelId: newFlow.id }
    })

    // Open Righty drawer
    rightyStore.openDrawer()

    // Always start a new conversation when clicking a chip prompt
    await rightyStore.startNewConversation()

    // Send a message as if the user is asking for help with their new quiz
    const userMessage = `I just created a new ${flowType.toLowerCase()} called "${newFlow.name}". What are some ideas for what I can do with this ${flowType.toLowerCase()}?`
    await rightyStore.sendMessage(userMessage)
  }
}
</script>

<style scoped>
.glow-trigger::before {
  content: '';
  background-image: linear-gradient(
    45deg,
    #d6d1d1,
    #f2e685,
    #fff,
    #e494bf,
    #63deb9,
    #fb9ccf,
    #458edf,
    #a96de5,
    #6ab2dc,
    #e894c1,
    #fff,
    #d6d1d1
  );
  position: absolute;
  top: -2px;
  left: -2px;
  background-size: 400%;
  z-index: 0;
  filter: blur(6px);
  width: calc(100% + 4px);
  height: calc(100% + 4px);
  animation: glowing 20s linear infinite;
  opacity: 0.6;
  border-radius: 9999px;
}

@keyframes glowing {
  0% {
    background-position: 0 0;
  }
  100% {
    background-position: 400% 0;
  }
}
</style>
